import * as React from 'react';
import * as ReactDom from 'react-dom';
import Styled from 'styled-deprecated';
import Photoswipe from 'photoswipe';
import 'photoswipe/dist/photoswipe.css';
import defaultStyles, {RootScss} from './root.scss';

/**
 * Photoswipe binds events on document, which has undesired effects when there are multiple instances active at the same time.
 * This method monkey patches photoswipe's internals to restrict event binding do root element
 */
export function bindEventsToRoot<P extends Photoswipe<any>>(photoswipe: P, root?: HTMLElement) {
    photoswipe.listen('firstUpdate', () => {
        const element = root || photoswipe.container.parentElement!;

        const {framework} = (photoswipe as any);
        const bind = framework.bind.bind(framework);

        const shouldBeContainer = (target: Element | Document | Window, type: string) => {
            if (/^key/.test(type)) {
                return false;
            }

            return (target === document || (target === window && (element as any)[`on${type}`] !== undefined));
        };

        framework.bind = (target: Element | Document | Window, types: string, ...rest: any[]) => {
            types.split(' ').forEach(type => {
                if (shouldBeContainer(target, type)) {
                    bind(element, type, ...rest);
                } else {
                    bind(target, type, ...rest);
                }
            });
        };

        const unbind = framework.unbind.bind(framework);

        framework.unbind = (target: Element | Document | Window, types: string, ...rest: any[]) => {

            types.split(' ').forEach(type => {
                if (shouldBeContainer(target, type)) {
                    unbind(element, type, ...rest);
                } else {
                    unbind(target, type, ...rest);
                }
            });
        };
    });

    return photoswipe;
}

export interface Props {
    forwardedRef?: React.Ref<HTMLDivElement>;
    styles: RootScss;
    overlay?: React.ReactNode;
    modal?: boolean;
}

class PhotoSwipeRoot extends React.PureComponent<Props> {
    static defaultProps = {
        modal: true,
    };

    node!: HTMLDivElement;
    promise?: Promise<any>;

    componentDidMount() {
        if (this.props.modal) {
            this.node = document.createElement('div');
            this.node.classList.add(...this.props.styles.root.split(' '));
            document.body.appendChild(this.node);

            this.forceUpdate();
        }
    }

    componentWillUnmount() {
        if (this.node && this.node.parentNode) {
            this.node.parentNode.removeChild(this.node);
        }
    }

    render() {
        const {styles, children, overlay, modal} = this.props;

        const element = (
            <div ref={this.props.forwardedRef} className={styles!.pswp} tabIndex={-1} role='dialog' aria-hidden='true'>
                <div className={styles!.bg} />
                <div className={styles!.scrollWrap}>
                    <div className={styles!.container}>
                        <div className={styles!.item} />
                        <div className={styles!.item} />
                        <div className={styles!.item} />
                    </div>
                    {children}
                </div>
                {overlay}
            </div>
        );

        if (!modal) {
            return element;
        }

        if (!this.node) {
            return null;
        }

        return ReactDom.createPortal(
            element,
            this.node,
        );
    }
}

export default Styled(defaultStyles)(PhotoSwipeRoot);
